import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import './image-track.scss';
import { ResizeCallbackData } from 'react-resizable';
import lodashDebounce from 'lodash.debounce';
import TrackWrapper from './track-wrapper';
import './track-header.scss';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import { scaleLinear } from 'd3-scale';
import { de } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';
import { set } from 'lodash';
import StripViewer from './image-strip-viewer';


// Define a more specific type for the items in the 'depths' array
interface DepthMetaData {
  startDepth: number;
  endDepth: number;
  fileName: string;
  // Add other properties if they exist
}

// Define a type for the 'track' object prop
interface TrackInfo {
  id: string | number;
  color: string;
  // Add other properties from the track object if used
}

// The exact interface for the ImageTrack component's props
interface ImageTrackProps {
  id: string | number;
  track: TrackInfo;
  topMargin: number;
  label: string;
  legend: boolean;
  legendRollover: boolean;
  depthUnit: number;
  apiUrl: string;
  clientId: number;
  projectId: number;
  boreholeId: number;
  startDepth: number | null | undefined;
  endDepth: number | null | undefined;
  imageType: number;
  byBoreholeDepth: boolean;
  authToken: string;
  availableHeight: number;
  headerBottomPadding: number;
  imageHeight: number;
  headerHeight: number;
  minHeaderHeight: number;
  trackWidth: number;
  setTrackWidth: (id: string | number, width: number) => void;
  scrollIntoView: boolean;
  trackHeaderClick: () => void;
  isSelected: boolean;
  needsRegenerating: boolean;
  setMetaDataDepth: (data: { depth: number; values: any[] } | null) => void;
  depths: DepthMetaData[];
  onZoom: (zoomFactor: number) => void;
  onPan: (deltaY: number) => void;
  onSetIsPanning: (isPanning: boolean) => void;  
  useStrips: boolean;
  noImage: boolean;
  isPanning: boolean;
  mouseWheelZoom: boolean;
  hasDeepZoom: boolean;
}

const ImageTrack = (props: ImageTrackProps) => {
  const { id,       
          track,
          topMargin,
          label, 
          legend,
          legendRollover,
          depthUnit,
          apiUrl, 
          clientId, 
          projectId, 
          boreholeId, 
          startDepth, 
          endDepth, 
          imageType, 
          byBoreholeDepth, 
          authToken, 
          availableHeight,
          headerBottomPadding,
          imageHeight,            
          headerHeight,
          minHeaderHeight,
          trackWidth,
          setTrackWidth,
          scrollIntoView, 
          trackHeaderClick,
          isSelected,            
          needsRegenerating,
          setMetaDataDepth,
          depths,
          onZoom,
          onPan,
          useStrips,
          noImage,
          onSetIsPanning,
          isPanning,
          mouseWheelZoom,
          hasDeepZoom
        } = props;

  const fieldRef = useRef<HTMLInputElement>(null);
  const history = useHistory();

  // DOM ref for the clickable image area (wraps StripViewer or <img>)
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Numeric refs for current start/end depths (avoid mixing with DOM refs)
  const startDepthValueRef = useRef<number | null>(null);
  const endDepthValueRef = useRef<number | null>(null);

  useEffect(() => {      
    if (scrollIntoView && fieldRef.current) {
      fieldRef.current.scrollIntoView({
        behavior: "smooth",
        block:"start",
        inline: "end"
      });
    }
  }, [scrollIntoView]);

  useEffect(() => {                  
    setImageLoaded(true);
  }, [startDepth, endDepth, imageType]);
  
  const [mousePosition, setMousePosition] = useState({left:0, top:0});
  const [imageLoaded, setImageLoaded] = useState(true);
  const [sortedDepths, setSortedDepths] = useState<any[]>([]);

  useEffect(() => {
    startDepthValueRef.current = startDepth ?? null;
  }, [startDepth]);

  useEffect(() => {
    endDepthValueRef.current = endDepth ?? null;
  }, [endDepth]);

  useEffect(() => {    
    let sortedDepths: any[] = depths ?  Array.from(depths) : [];
    
    sortedDepths?.sort((a: any, b: any) => b.startDepth - a.startDepth);   

    if (depthUnit === 2) {
      
      function convertDepths(depths: any[]) {
        return depths.map(d => ({
          ...d,
          startDepth: d.startDepth * 3.2808,
          endDepth: d.endDepth * 3.2808
        }));
      }

      sortedDepths = convertDepths(sortedDepths);
    }

    setSortedDepths(sortedDepths);
  }, [depths, depthUnit]);
  
  const throttledListener = useRef<(e: React.MouseEvent<HTMLDivElement>, sortedDepths:any[]) => void>();

  throttledListener.current = useCallback(
    throttle((e: React.MouseEvent<HTMLDivElement>, sortedDepths: any[]) => {
      imageOnMouseMove(e, sortedDepths);
    }, 100), // 200 ms throttle delay (adjust as needed)
    []
  );

  const imageOnMouseMove = (event: any, sortedDepths: any[]) => {
    const container = imageContainerRef.current;
    if (!container || startDepthValueRef.current == null || endDepthValueRef.current == null) return;

    const bounds = container.getBoundingClientRect();
    let y = event.clientY - bounds.top;
    if (y < 0) y = 0;
    if (y > bounds.height) y = bounds.height;

    const yScale = scaleLinear()
      .domain([0, bounds.height])
      .range([startDepthValueRef.current, endDepthValueRef.current]);

    const scaledDepth = yScale(y);

    const foundDepth = sortedDepths.find((d: any) => scaledDepth < d.endDepth && scaledDepth >= d.startDepth);

    const newPosition = { left: event.pageX - 60, top: event.pageY - event.target.offsetTop - 60 };
    setMousePosition(newPosition);
    const values: any[] = [];
    if (foundDepth) {
      values.push({ displayName: 'Filename', value: foundDepth.fileName });
    }
    setMetaDataDepth({ depth: scaledDepth, values });
  };

  const imageOnMouseOut = () => {
    setMetaDataDepth(null);
  };

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noreferrer");
  };

  const linkToImageViewerAtEvent = (event: React.MouseEvent, sorted: any[]) => {
    const container = imageContainerRef.current;
    if (!container || startDepthValueRef.current == null || endDepthValueRef.current == null) return;

    const bounds = container.getBoundingClientRect();
    let y = event.clientY - bounds.top;
    if (y < 0) y = 0;
    if (y > bounds.height) y = bounds.height;

    const yScale = scaleLinear()
      .domain([0, bounds.height])
      .range([startDepthValueRef.current, endDepthValueRef.current]);

    const scaledDepth = yScale(y);
    const foundDepth = sorted.find((d: any) => scaledDepth < d.endDepth && scaledDepth >= d.startDepth);
    const foundIndexFromEnd = sorted.findIndex((d: any) => scaledDepth < d.endDepth && scaledDepth >= d.startDepth);
    const foundIndex = sorted.length - foundIndexFromEnd - 1;

    if (foundDepth) {
      const url = `${window.location.origin}/client/${clientId}/project/${projectId}/borehole/${boreholeId}/image-viewer?imageNumber=${foundIndex}&fileName=${foundDepth.fileName}&imageTypeId=${imageType}`;
      window.open(url, '_blank', 'noreferrer');
    }
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {    
    if (hasDeepZoom && imageContainerRef.current && imageContainerRef.current.contains(e.target as Node)) {
      linkToImageViewerAtEvent(e, sortedDepths);
    }
  };

  const headerPadding = () => {
    
    const totalHeight = headerHeight - 12;
    const padding = minHeaderHeight - totalHeight;
    if (padding > 0) {
      return <div style={{minHeight: padding}}></div>
    }
    
    return null;
  };

  const onResizeDebounce = (e: SyntheticEvent, data: ResizeCallbackData) => {      
    if (data.size.width) {
      setTrackWidth(id, data.size.width);
    }
  };
  
  const onResize = useCallback(lodashDebounce(onResizeDebounce, 40), []);      

  //let marginTop = (legend && !legendRollover ? 72 : topMargin);
  let marginTop = topMargin - (legend && !legendRollover ? 11 : 0);

  const imageError = () => {        
    setImageLoaded(false);
  };

  function throttle<T extends (...args: any[]) => void>(func: T, delay: number) {
    let lastCall = 0;
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  }

  return (
    <>
      {imageLoaded && !noImage && (
        <TrackWrapper
          trackWidth={trackWidth}
          onResize={onResize}
          minWidth={40}
          chartHeight={availableHeight - headerBottomPadding}
          scrollIntoView={scrollIntoView}
          editMode={isSelected}
          // Route genuine clicks to our handler (TrackWrapper already distinguishes drag vs click)
          trackClick={handleTrackClick}
          onZoom={onZoom}
          onPan={onPan}
          onSetIsPanning={onSetIsPanning}
          mouseWheelZoom={mouseWheelZoom}
          startDepth={startDepth ?? 0}
          endDepth={endDepth ?? 0}
        >
          <>
            {headerPadding()}
            <div
              className={`has-text-centered track-label cumulative-chart-header ${
                isSelected ? 'highlighted-track' : ''
              }`}
              style={{ backgroundColor: track.color }}
            >
              <div
                className="track-header cumulative-chart-curve"
                data-tooltip-id={`image-header-${track.id}`}
              >
                {label}{' '}
                {needsRegenerating && (
                  <span
                    className="material-icons warning-icon track-header-icon"
                    title={'Need regenerating'}
                  >
                    warning
                  </span>
                )}
                {legend && !legendRollover && (
                  <div>
                    <img
                      className="image-legend"
                      src={`${apiUrl}client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/legend?imageType=${imageType}&access_token=${authToken}`}
                    />
                  </div>
                )}
              </div>
            </div>
            {createPortal(
              <Tooltip id={`image-header-${track.id}`} className="reacttooltip" html={label} />,
              document.body
            )}
            {startDepth != null && endDepth != null && (
              <div
                // This wraps the image area (StripViewer or <img>) used for click hit-testing
                ref={imageContainerRef}
                style={{ marginTop: marginTop }}
                className={`${legendRollover ? 'image-tooltip' : ''}`}
              >
                {useStrips && (
                  <StripViewer
                    initialStartDepthMeters={startDepth}
                    initialEndDepthMeters={endDepth}
                    clientId={clientId}
                    projectId={projectId}
                    boreholeId={boreholeId}
                    imageTypeId={imageType}
                    authToken={authToken}
                    viewerDisplayHeightPx={imageHeight}
                    viewerWidth={trackWidth}
                    isPanning={isPanning}
                    onMouseMove={(e) => throttledListener.current?.(e, sortedDepths)}                    
                    onMouseOut={imageOnMouseOut}
                  />
                )}

                {!useStrips && (
                  <img
                    src={`${apiUrl}client/${clientId}/project/${projectId}/borehole/${boreholeId}/image?startDepth=${startDepth}&endDepth=${endDepth}&imageType=${imageType}&byBoreholeDepth=${byBoreholeDepth}&depthUnit=${depthUnit}&access_token=${authToken}`}
                    className="borehole"
                    onError={imageError}
                    style={{ height: imageHeight, width: trackWidth }}
                    onMouseMove={(e) => throttledListener.current?.(e, sortedDepths)}                    
                    onMouseOut={imageOnMouseOut}
                  />
                )}
              </div>
            )}
          </>
        </TrackWrapper>
      )}
    </>
  );
};

export default ImageTrack;